import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Building2,
  User,
  MapPin,
  Clock,
  Save,
  Plus,
  Trash2,
  Loader2,
  Check
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const daysOfWeek = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const ClinicSetup = () => {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newService, setNewService] = useState({ name: '', duration_minutes: 30, price: '' });

  useEffect(() => {
    loadClinic();
  }, []);

  const loadClinic = async () => {
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    console.log('🔍 Loading clinic with:', { clinicId, tokenExists: !!token });
    
    if (!clinicId || !token) {
      console.error('❌ Missing clinicId or token');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/clinic/${clinicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Clinic data received:', response.data?.name);
      
      // Initialize schedule if empty
      if (!response.data.schedule || response.data.schedule.length === 0) {
        response.data.schedule = daysOfWeek.map(day => ({
          day: day.key,
          is_open: day.key !== 'sunday',
          open_time: '09:00',
          close_time: '18:00'
        }));
      }
      
      // Initialize location if empty
      if (!response.data.location) {
        response.data.location = { address: '', city: '', country: 'México' };
      }
      
      setClinic(response.data);
    } catch (error) {
      console.error('❌ Error loading clinic:', error.response?.status, error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Prevent saving empty data
    if (!clinic.name || clinic.name.trim() === '') {
      alert('El nombre de la clínica es requerido');
      return;
    }
    
    setSaving(true);
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    try {
      // Only send non-empty fields to prevent overwriting with empty values
      const dataToSave = {};
      if (clinic.name) dataToSave.name = clinic.name;
      if (clinic.specialty) dataToSave.specialty = clinic.specialty;
      if (clinic.description) dataToSave.description = clinic.description;
      if (clinic.phone) dataToSave.phone = clinic.phone;
      if (clinic.phone_secondary) dataToSave.phone_secondary = clinic.phone_secondary;
      if (clinic.phone_emergency) dataToSave.phone_emergency = clinic.phone_emergency;
      if (clinic.website) dataToSave.website = clinic.website;
      if (clinic.notification_email) dataToSave.notification_email = clinic.notification_email;
      if (typeof clinic.silent_followups_enabled === 'boolean') dataToSave.silent_followups_enabled = clinic.silent_followups_enabled;
      if (clinic.location) dataToSave.location = clinic.location;
      if (clinic.schedule) dataToSave.schedule = clinic.schedule;
      if (clinic.doctor_name) dataToSave.doctor_name = clinic.doctor_name;
      if (clinic.doctor_title) dataToSave.doctor_title = clinic.doctor_title;
      if (clinic.doctor_license) dataToSave.doctor_license = clinic.doctor_license;
      if (clinic.doctor_bio) dataToSave.doctor_bio = clinic.doctor_bio;
      if (clinic.services && clinic.services.length > 0) dataToSave.services = clinic.services;
      dataToSave.msi_enabled = clinic.msi_enabled;
      if (clinic.msi_months) dataToSave.msi_months = clinic.msi_months;
      if (clinic.privacy_notice_url) dataToSave.privacy_notice_url = clinic.privacy_notice_url;
      
      await axios.patch(`${API}/clinic/${clinicId}`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.name) return;
    
    const service = {
      id: crypto.randomUUID(),
      name: newService.name,
      duration_minutes: parseInt(newService.duration_minutes) || 30,
      price: newService.price ? parseFloat(newService.price) : null,
      is_active: true
    };
    
    setClinic(prev => ({
      ...prev,
      services: [...(prev.services || []), service]
    }));
    setNewService({ name: '', duration_minutes: 30, price: '' });
  };

  const handleRemoveService = (serviceId) => {
    setClinic(prev => ({
      ...prev,
      services: prev.services.filter(s => s.id !== serviceId)
    }));
  };

  const updateSchedule = (dayKey, field, value) => {
    setClinic(prev => ({
      ...prev,
      schedule: prev.schedule.map(s =>
        s.day === dayKey ? { ...s, [field]: value } : s
      )
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Mi Clínica">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Mi Clínica">
      <div className="max-w-4xl space-y-6">
        {/* Basic Info */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Información Básica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Nombre de la clínica</Label>
                <Input
                  value={clinic?.name || ''}
                  onChange={(e) => setClinic({ ...clinic, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Especialidad</Label>
                <Input
                  value={clinic?.specialty || ''}
                  onChange={(e) => setClinic({ ...clinic, specialty: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Descripción</Label>
              <Textarea
                value={clinic?.description || ''}
                onChange={(e) => setClinic({ ...clinic, description: e.target.value })}
                placeholder="Describe tu clínica..."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Teléfono principal</Label>
                <Input
                  value={clinic?.phone || ''}
                  onChange={(e) => setClinic({ ...clinic, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Teléfono secundario</Label>
                <Input
                  value={clinic?.phone_secondary || ''}
                  onChange={(e) => setClinic({ ...clinic, phone_secondary: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Sitio web</Label>
              <Input
                value={clinic?.website || ''}
                onChange={(e) => setClinic({ ...clinic, website: e.target.value })}
                placeholder="https://..."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400">Correo para avisos</Label>
              <Input
                type="email"
                value={clinic?.notification_email || ''}
                onChange={(e) => setClinic({ ...clinic, notification_email: e.target.value })}
                placeholder={clinic?.email || 'avisos@tuclinica.com'}
                className="bg-white/5 border-white/10 text-white mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Aquí llegan citas agendadas, pacientes que piden atención humana y leads calientes. Si lo dejas vacío, usamos el correo de tu cuenta.</p>
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-300">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!clinic?.silent_followups_enabled}
                onChange={(e) => setClinic({ ...clinic, silent_followups_enabled: e.target.checked })}
              />
              <span>
                Seguimiento automático a pacientes que dejan de contestar
                <span className="block text-xs text-gray-500">Tu asistente les escribe una vez al día siguiente y otra a los 3 días si no agendaron. Nunca a quien pidió no recibir mensajes.</span>
              </span>
            </label>
            
            {/* Urgencias y Pagos */}
            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div>
                <Label className="text-gray-400">📞 Teléfono de URGENCIAS</Label>
                <Input
                  value={clinic?.phone_emergency || ''}
                  onChange={(e) => setClinic({ ...clinic, phone_emergency: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">La IA dará este número en emergencias</p>
              </div>
              <div>
                <Label className="text-gray-400">🔗 Aviso de Privacidad (URL)</Label>
                <Input
                  value={clinic?.privacy_notice_url || ''}
                  onChange={(e) => setClinic({ ...clinic, privacy_notice_url: e.target.value })}
                  placeholder="https://tuclínica.com/aviso-privacidad"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            {/* MSI */}
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clinic?.msi_enabled || false}
                    onChange={(e) => setClinic({ ...clinic, msi_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
                <span className="text-white">💳 Acepto Meses Sin Intereses (MSI)</span>
              </div>
              {clinic?.msi_enabled && (
                <div className="mt-3">
                  <Label className="text-gray-400">¿Hasta cuántos meses?</Label>
                  <Input
                    type="text"
                    value={clinic?.msi_months || '3, 6, 12'}
                    onChange={(e) => setClinic({ ...clinic, msi_months: e.target.value })}
                    placeholder="3, 6, 12"
                    className="bg-white/5 border-white/10 text-white mt-1 w-48"
                  />
                  <p className="text-xs text-gray-500 mt-1">La IA mencionará esto cuando pregunten por pagos</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Doctor Info */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Información del Médico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-400">Título</Label>
                <Input
                  value={clinic?.doctor_title || ''}
                  onChange={(e) => setClinic({ ...clinic, doctor_title: e.target.value })}
                  placeholder="Dr."
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-gray-400">Nombre completo</Label>
                <Input
                  value={clinic?.doctor_name || ''}
                  onChange={(e) => setClinic({ ...clinic, doctor_name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Cédula profesional</Label>
              <Input
                value={clinic?.doctor_license || ''}
                onChange={(e) => setClinic({ ...clinic, doctor_license: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-400">Biografía</Label>
              <Textarea
                value={clinic?.doctor_bio || ''}
                onChange={(e) => setClinic({ ...clinic, doctor_bio: e.target.value })}
                placeholder="Experiencia, formación, etc."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              Ubicación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-400">Dirección</Label>
              <Input
                value={clinic?.location?.address || ''}
                onChange={(e) => setClinic({ ...clinic, location: { ...clinic.location, address: e.target.value } })}
                placeholder="Calle, número, colonia..."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-400">Ciudad</Label>
                <Input
                  value={clinic?.location?.city || ''}
                  onChange={(e) => setClinic({ ...clinic, location: { ...clinic.location, city: e.target.value } })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Estado</Label>
                <Input
                  value={clinic?.location?.state || ''}
                  onChange={(e) => setClinic({ ...clinic, location: { ...clinic.location, state: e.target.value } })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400">Código postal</Label>
                <Input
                  value={clinic?.location?.postal_code || ''}
                  onChange={(e) => setClinic({ ...clinic, location: { ...clinic.location, postal_code: e.target.value } })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Referencias (cómo llegar)</Label>
              <Textarea
                value={clinic?.location?.references || ''}
                onChange={(e) => setClinic({ ...clinic, location: { ...clinic.location, references: e.target.value } })}
                placeholder="Cerca de..., frente a..."
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Servicios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Service List */}
            {clinic?.services?.map((service) => (
              <div key={service.id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{service.name}</p>
                  <p className="text-gray-500 text-sm">
                    {service.duration_minutes} min
                    {service.price && ` • $${service.price}`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveService(service.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}

            {/* Add Service Form */}
            <div className="flex gap-2 pt-4 border-t border-white/10">
              <Input
                placeholder="Nombre del servicio"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white flex-1"
              />
              <Input
                type="number"
                placeholder="Min"
                value={newService.duration_minutes}
                onChange={(e) => setNewService({ ...newService, duration_minutes: e.target.value })}
                className="bg-white/5 border-white/10 text-white w-20"
              />
              <Input
                type="number"
                placeholder="Precio"
                value={newService.price}
                onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                className="bg-white/5 border-white/10 text-white w-24"
              />
              <Button onClick={handleAddService} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Horarios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {daysOfWeek.map((day) => {
              const schedule = clinic?.schedule?.find(s => s.day === day.key) || {};
              return (
                <div key={day.key} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                  <div className="w-24">
                    <span className="text-white font-medium">{day.label}</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={schedule.is_open !== false}
                      onChange={(e) => updateSchedule(day.key, 'is_open', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-gray-400 text-sm">Abierto</span>
                  </label>
                  {schedule.is_open !== false && (
                    <>
                      <Input
                        type="time"
                        value={schedule.open_time || '09:00'}
                        onChange={(e) => updateSchedule(day.key, 'open_time', e.target.value)}
                        className="bg-white/5 border-white/10 text-white w-32"
                      />
                      <span className="text-gray-500">a</span>
                      <Input
                        type="time"
                        value={schedule.close_time || '18:00'}
                        onChange={(e) => updateSchedule(day.key, 'close_time', e.target.value)}
                        className="bg-white/5 border-white/10 text-white w-32"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saved ? 'Guardado' : 'Guardar cambios'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClinicSetup;
